import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CButton, CCol, CForm, CRow, CLink } from '@coreui/react';
import { Formik, ErrorMessage } from 'formik';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInput, CInputCheckbox, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import { useDispatch, useSelector } from 'react-redux';
import { setAsideShow, setFlexibleMultiModal } from '../../../../../../actions/common';
import { ADD_USER, API_AI_INSIGHTS_TEMPLATE_SCHEDULES, COMPONENT_NAME, DEFAULT_DATE_FORMAT } from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import { fetchAccountUsers, setManagers, setShowBlockAccountPopup, toggleAsideLimitationHit } from '../../../../../../actions/subscriber';
import Select, { components } from 'react-select';
import { toast } from 'react-toastify';
import { InviteUserPopup } from '../../../../../general/popup';
import { toastError } from '../../../../../../utils';
import * as Yup from 'yup';
import CIcon from '@coreui/icons-react';
import OverwriteReport from '../Popup/OverwriteReport';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { buildReportOptionLabel } from '../../../../../../utils/aiInsightDataSources';

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const scheduleOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly on' },
    { value: 'monthly', label: 'Monthly on' },
    { value: 'quarterly', label: 'Quarterly (calendar)' },
];

const DELIVERY_SCHEDULE = {
    monthly: 'Month',
    quarterly: 'Quarter',
};

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required('Schedule Name is required')
        .max(100, 'Schedule Name cannot exceed 100 characters'), // Example max length
    schedule: Yup.string()
        .required('Schedule cadence selection is required'),
    specificDay: Yup.string().when('schedule', {
        // Make 'specificDay' required only if 'schedule' is 'monthly' or 'quarterly'
        is: (scheduleValue) => ['monthly', 'quarterly'].includes(scheduleValue),
        then: (schema) => schema.required('Please select the day (First/Last) for the schedule'),
        otherwise: (schema) => schema.notRequired(), // Not required for other schedule types
    }),
    daysOfWeek: Yup.array().when('schedule', {
         // Make 'daysOfWeek' required only if 'schedule' is 'weekly'
        is: 'weekly',
        then: (schema) => schema
            .min(1, 'Please select at least one day for the weekly schedule')
            .required('Day selection is required for weekly schedule'), // Should technically be covered by min(1)
        otherwise: (schema) => schema.notRequired(), // Not required for other schedule types
    }),
    isSendMail: Yup.boolean().oneOf([true], 'Selecting email delivery is required.'), // No specific validation needed unless required
    isOverwriteDateRange: Yup.boolean(), // No specific validation needed unless required
    emailList: Yup.array().when('isSendMail', {
        // Make 'emailList' required and non-empty only if 'isSendMail' is true
        is: true,
        then: (schema) => schema
            .min(1, 'Please select at least one recipient')
            .required('Recipient selection is required when sending email'), // Technically covered by min(1)
        otherwise: (schema) => schema.notRequired(), // Not required if not sending email
    }),
    customSubject: Yup.string()
    .trim()
    .when('isSendMail', {
      is: true,
      then: (schema) => schema.required('Custom subject is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    optionalMessage: Yup.string()
         .trim()
});

const ScheduleTemplate = ({ id, attributes }) => {
    const dispatch = useDispatch();
    const { schedule, setViewSchedule, setViewTemplates, dataSources, handleSetUnsavedChanges } = attributes;
    const owner = useSelector((state) => state.subscriber.owner);
    const managers = useSelector((state) => state.subscriber.managers);
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);
    const { numberOfUsers: usedUsers, packageNumberOfUsers: maxUsers } = activeAccount;
    const [defaultPlaceholderTo, setDefaultPlaceholderTo] = useState('Select recipients');
    const [privateLoading, setPrivateLoading] = useState(false);
    const [modalInviteUser, setModalInviteUser] = useState(false);
    const [validateInviteEmail, setValidateInviteEmail] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [showOverwrite, setShowOverwrite] = useState({});
    const [dateRangeOverride, setDateRangeOverride] = useState(
        Array.isArray(schedule?.dateRangeOverrideSettings) ? schedule.dateRangeOverrideSettings : []
    );
    const emailRecipients = typeof schedule?.emailRecipients === 'string' ? JSON.parse(schedule?.emailRecipients) : schedule?.emailRecipients;

    const scheduleId = schedule?.id;
    const initialValues = {
        name: schedule?.name?.trim() || '',
        schedule: schedule?.schedule || '',
        specificDay: schedule?.specificDay || '',
        daysOfWeek: Array.isArray(schedule?.daysOfWeek) ? schedule.daysOfWeek : [],
        isSendMail: true,
        isOverwriteDateRange: schedule?.overrideDateRange || false,
        emailList: emailRecipients || [],
        customSubject: schedule?.emailSubject || '',
        optionalMessage: schedule?.emailCustomMessage || '',
        dateRangeOverrideSettings: dateRangeOverride || null,
    };

    const emailsOfAccountUsers = useMemo(() => {
        return owner ? [owner, ...managers].map(user => ({
            label: user.email,
            value: user.email
        })) : [];
    }, [owner, managers]);

    useEffect(() => {
        dispatch(fetchAccountUsers(activeAccount.id));
    }, []); // eslint-disable-line

    const closeModal = useCallback(() => {
        dispatch(setFlexibleMultiModal({
            show: false,
            component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE_SCHEDULE,
        }));
    }, [dispatch]);
    
    const onSubmit = useCallback(async (values) => {
        const {
            name,
            schedule,
            specificDay,
            daysOfWeek,
            isSendMail,
            isOverwriteDateRange,
            emailList,
            customSubject,
            optionalMessage,
        } = values;
     
        const payload = {
            name: name,
            accountId: activeAccount.accountId,
            templateId: id,
            schedule,
            specificDay,
            daysOfWeek,
            sendEmail: isSendMail,
            emailRecipients: JSON.stringify(emailList),
            emailSubject: customSubject?.trim() || '',
            emailCustomMessage: optionalMessage?.trim() || '',
            overrideDateRange: isOverwriteDateRange,
            dateRangeOverrideSettings: isOverwriteDateRange
                ? (Array.isArray(dateRangeOverride) ? dateRangeOverride : []).map((item) => {
                        const newItem = { ...item };
                        const dataDateRangeConfig = newItem.dateRangeConfig;

                        const customCompare = dataDateRangeConfig?.rolling?.customDateCompare;
                        const selection = customCompare?.selection;

                        if (selection?.startDate && selection?.endDate) {
                            selection.startDate = dayjs(selection.startDate).format(DEFAULT_DATE_FORMAT);
                            selection.endDate = dayjs(selection.endDate).format(DEFAULT_DATE_FORMAT);
                        }

                        return newItem;
                })
                : [],
        };
      
        try {
            let response;

            if (scheduleId) {
                response = await callTokenApi(
                    `${API_AI_INSIGHTS_TEMPLATE_SCHEDULES}/${scheduleId}`,
                    'PUT',
                    payload
                );
            } else {
                response = await callTokenApi(
                    API_AI_INSIGHTS_TEMPLATE_SCHEDULES,
                    'POST',
                    payload
                );
            }

            const isSuccess = response?.status === 200 || response?.status === 201 || [200, 201].includes(response?.data?.code);
            const newSchedule = response?.data?.data;

            if (isSuccess && newSchedule) {
                setViewSchedule((prev) => {
                    if (scheduleId) {
                        return prev.map(item => item.id === scheduleId ? newSchedule : item);
                    }

                    return [newSchedule, ...prev];
                });

                const newScheduleUpdated = {
                    id: newSchedule.id,
                    emailRecipients: newSchedule.emailRecipients,
                    name: newSchedule.name,
                    status: newSchedule.status,
                    sendEmail: newSchedule.sendEmail,
                    daysOfWeek: newSchedule.daysOfWeek,
                    schedule: newSchedule.schedule,
                    specificDay: newSchedule.specificDay,
                };

                setViewTemplates((prev) =>
                    prev.map((tpl) => {
                        if (tpl.id !== id) return tpl;

                        const schedules = tpl.schedules || [];

                        const updatedSchedules = scheduleId
                            ? schedules.map((s) =>
                                s.id === scheduleId
                                    ? newScheduleUpdated
                                    : s
                                )
                            : [
                                newScheduleUpdated,
                                ...schedules,
                            ];

                        return { ...tpl, schedules: updatedSchedules };
                    })
                );

                toast.success(`Schedule ${scheduleId ? 'updated' : 'created'} successfully.`);
                handleSetUnsavedChanges && handleSetUnsavedChanges(true);
                closeModal();
            } else {
                toast.error('Failed to save schedule configuration.');
            }
        } catch (error) {
            console.error('Schedule save error:', error);
            toast.error('An error occurred while saving. Please try again later.');
        }

    }, [id, closeModal, scheduleId, setViewSchedule, setViewTemplates, activeAccount.accountId, handleSetUnsavedChanges, dateRangeOverride]);

    const MenuList = (props) => {
        return (
            <components.MenuList {...props}>
                {props.children}
            </components.MenuList>
        );
    };

    const noOptionsMessage = () => {
        return (
            <span>
                No option.
            </span>
        );
    };

    const toggleModalInviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }
        setModalInviteUser(prev => !prev);
        setValidateInviteEmail('');
    }, [dispatch, usedUsers, maxUsers]);

    const closeModalInviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }
        setModalInviteUser(false);
        setValidateInviteEmail('');
    }, [dispatch, usedUsers, maxUsers]);
    
    const inviteUser = useCallback(() => {
        if (usedUsers >= maxUsers && maxUsers !== -1) {
            toggleModalInviteUser();
            dispatch(toggleAsideLimitationHit());
            dispatch(setAsideShow(true));
            return;
        }

        const validateSchema = Yup.object().shape({
            newUserEmail: Yup.string()
                .required('Email is required')
                .email('Email invalid')
                .test('checkSpecialCharacters', 'Email invalid', value =>
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)),
        });

        validateSchema
            .validate({ newUserEmail })
            .then(() => {
                setValidateInviteEmail('');

                if (activeAccount) {
                    const data = {
                        email: newUserEmail,
                        accountId: activeAccount.id,
                    };
                    setPrivateLoading(true);

                    callTokenApi(`${ADD_USER}`, 'POST', data).then((response) => {
                        setPrivateLoading(false);

                        if (response.status === 200) {
                            if (response.data.useracc) {
                                dispatch(setManagers([...managers, response.data.user]));
                            }

                            toast.success(response.data.message);
                            setNewUserEmail('');
                            toggleModalInviteUser();
                        } else {
                            response.data.accountBlocked
                                ? dispatch(setShowBlockAccountPopup(true))
                                : toastError(response);
                            toggleModalInviteUser();
                        }
                    });
                }
            })
            .catch(err => setValidateInviteEmail(err.message));
    }, [dispatch, managers, newUserEmail, activeAccount, usedUsers, maxUsers, toggleModalInviteUser]);

    const submitNewUserEmail = (e) => {
        if (e.keyCode === 13) {
            inviteUser();
        }
    };

    const handleNewUserEmail = (e) => {
        setNewUserEmail(e.target.value);
        setValidateInviteEmail('');
    };

    const isDataChanged = (currentValues, initialValues) => {
        return JSON.stringify(currentValues) !== JSON.stringify(initialValues);
    };

    return (
        <div className="rule-detail account-connected-detail schedule-detail">
            <div className="cvr-create-new">
                <h1 className='mb-3'>{schedule ? `Edit Schedule: ${schedule?.name}` : 'Create Schedule'}</h1>
                <p>Manage the settings for the recurring schedule that this</p>
            </div>

            <OverwriteReport
                show={!!Object.keys(showOverwrite).length > 0}
                type="report"
                data = {showOverwrite}
                dateRangeOverride={dateRangeOverride}
                setDateRangeOverride={setDateRangeOverride}
                setShowOverwrite={setShowOverwrite}
                onCancel={() => setShowOverwrite({})}
                dataSources={dataSources}
            />

            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                validationSchema={validationSchema}
            >
                {({ values, errors, setErrors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => {
                    const optionFirstLast =
                         values.schedule && ['monthly', 'quarterly'].includes(values.schedule)
                              ? [
							{ value: 'first', label: `First Day of the ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'both' },
							{ value: 'last', label: `Last Day of the ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'both' },
							{ value: 'firstMonday', label: `First Monday of Each ${DELIVERY_SCHEDULE[values.schedule]}`, type: 'monthly' },
                                ]
                              : [];

                    const handleDayToggle = (day) => {
                        const isChecked = values.daysOfWeek.includes(day);

                        const updatedDays = isChecked
                            ? values.daysOfWeek.filter((d) => d !== day)
                            : [...values.daysOfWeek, day];

                        if (updatedDays.length === 0) {
                            toast.error('Select at least 1 option!');
                        } else {
                            setFieldValue('daysOfWeek', updatedDays);
                        }
                    };

                    return (
                         <CForm onSubmit={handleSubmit} noValidate>
                              <CRow>
                                   <CCol lg="6">
                                        <CFormGroup>
                                             <CLabel htmlFor="name">
                                                  {' '}
                                                  <strong>Schedule Name: </strong>
                                             </CLabel>

                                             <CInput
                                                  id="name"
                                                  name="name"
                                                  placeholder="Schedule Name (only used to manage schedules)"
                                                  invalid={!!errors.name && touched.name}
                                                  required
                                                  value={values.name}
                                                  onChange={(e) => {
                                                       setFieldValue('name', e.target.value);

                                                       if (errors) {
                                                            setErrors({ ...errors, name: '' });
                                                       }
                                                  }}
                                             />
                                             <CInvalidFeedback>
                                                  <ErrorMessage name="name" />
                                             </CInvalidFeedback>
                                        </CFormGroup>
                                   </CCol>
                              </CRow>

                              <CRow>
                                   <CCol lg="6">
                                        <CFormGroup>
                                             <CLabel htmlFor="schedule">
                                                  <div className="title">Choose the cadence you'd like to use to run the insights.</div>
                                             </CLabel>

                                             <Select
                                                  id="schedule"
                                                  name="schedule"
                                                  placeholder="Select a schedule"
                                                  className={`react-select ${!!errors.schedule && touched.schedule ? 'is-invalid' : ''}`}
                                                  options={scheduleOptions}
                                                  value={scheduleOptions.find((option) => option.value === values.schedule)}
                                                  onChange={(e) => {
                                                       setFieldValue('schedule', e.value || '');
                                                       if (e.value === 'weekly' && !values.daysOfWeek?.length) {
                                                            setFieldValue('daysOfWeek', ['Sunday']);
                                                       }
                                                  }}
                                             />

                                             <CInvalidFeedback>
                                                  <ErrorMessage name="schedule" />
                                             </CInvalidFeedback>
                                        </CFormGroup>
                                   </CCol>
                              </CRow>

                              <CRow>
                                   {optionFirstLast.length > 0 && (
                                        <CCol lg="6">
                                             <CFormGroup>
                                                  <Select
                                                       id="specificDay"
                                                       name="specificDay"
                                                       placeholder="Select an option"
                                                       className={`react-select ${!!errors.specificDay && touched.specificDay ? 'is-invalid' : ''}`}
                                                       options={optionFirstLast.filter((option) => {
                                                            if (values.schedule === 'monthly') {
                                                                 return true;
                                                            } else {
                                                                 return option.type === 'both';
                                                            }
                                                       })}
                                                       value={optionFirstLast.find((option) => option.value === values.specificDay)}
                                                       onChange={(e) => setFieldValue('specificDay', e.value || '')}
                                                  />
                                                  <CInvalidFeedback>
                                                       <ErrorMessage name="specificDay" />
                                                  </CInvalidFeedback>
                                             </CFormGroup>
                                        </CCol>
                                   )}

                                   {values.schedule === 'weekly' && (
                                        <CCol lg="6">
                                             <CFormGroup>
                                                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                                       {days.map((day, index) => (
                                                            <CButton
                                                                 key={index}
                                                                 onClick={() => handleDayToggle(day)}
                                                                 className={`day-of-week ${values.daysOfWeek.includes(day) ? 'checked' : ''}`}
                                                            >
                                                                 {day.charAt(0)}
                                                            </CButton>
                                                       ))}
                                                  </div>
                                             </CFormGroup>
                                        </CCol>
                                   )}
                              </CRow>

                              <CRow className="mt-3 gap-2">
                                   <CCol lg="6">
                                        <CFormGroup variant="custom-checkbox" inline>
                                             <CInputCheckbox
                                                  custom
                                                  id="isOverwriteDateRange"
                                                  name="isOverwriteDateRange"
                                                  checked={values.isOverwriteDateRange}
                                                  className="m-auto"
                                                  onChange={handleChange}
                                             />
                                             <CLabel htmlFor="isOverwriteDateRange" variant="custom-checkbox">
                                                  Overwrite the date range settings on the data report inputs
                                             </CLabel>
                                        </CFormGroup>
                                   </CCol>

                                   {values.isOverwriteDateRange && dataSources && (
                                        <CCol lg="6">
                                             {dataSources.map((dateSource, index) => {
                                                  const descriptionOverride = JSON.parse(dateSource.descriptionOverride);
                                                  const reportLabel = buildReportOptionLabel({
                                                       name: descriptionOverride.reportName,
                                                       accountName: dateSource.accountName,
                                                       isCrossAccount: !!activeAccount.accountId && dateSource.accountId !== activeAccount.accountId,
                                                  });
                                                  return (
                                                       <CRow className="d-flex align-items-center" key={index}>
                                                            <CCol lg="3" className="text-right">
                                                                 <strong>{reportLabel}</strong>
                                                            </CCol>

                                                            <CCol lg="9">
                                                                 <div className="d-flex align-items-center">
                                                                      <CButton
                                                                           type="button"
                                                                           color="link"
                                                                           className="text-dark text-underline text-capitalize"
                                                                           onClick={() => setShowOverwrite(dateSource)}
                                                                      >
                                                                           Modify Rolling Date Range
                                                                      </CButton>

                                                                      <CLink
                                                                           className="option text-primary"
                                                                           onClick={() => setShowOverwrite(dateSource)}
                                                                      >
                                                                           <CIcon icon="icon-pencil" height={18} />
                                                                      </CLink>
                                                                 </div>
                                                            </CCol>
                                                       </CRow>
                                                  );
                                             })}
                                        </CCol>
                                   )}

                                   <CCol lg="6">
                                        <CLabel htmlFor="isSendMail">Deliver the insights via email</CLabel>
                                   </CCol>

                                   {values.isSendMail && (
                                        <>
                                             <CCol lg="6">
                                                  <CFormGroup>
                                                       <CLabel htmlFor="emailList">
                                                            <div className="title">
                                                                 <div>
                                                                      <strong>Select Recipients: </strong>
                                                                 </div>
                                                                 Recipients must be users inside your ListenLayer account.
                                                            </div>
                                                       </CLabel>

                                                       <Select
                                                            id="emailList"
                                                            classNamePrefix="react-select"
                                                            isMulti
                                                            name="emailList"
                                                            options={emailsOfAccountUsers}
                                                            noOptionsMessage={noOptionsMessage}
                                                            value={values.emailList}
                                                            onChange={(emails) => setFieldValue('emailList', emails || [])}
                                                            placeholder="Select recipients"
                                                            className={!!errors.emailList && touched.emailList ? 'is-invalid' : ''}
                                                            components={{ MenuList }}
                                                            isSearchable={defaultPlaceholderTo === 'Select recipients' ? false : true}
                                                            onMenuOpen={() => setDefaultPlaceholderTo('Type here to filter the list')}
                                                       />

                                                       <CInvalidFeedback>
                                                            <ErrorMessage name="emailList" />
                                                       </CInvalidFeedback>

                                                       <div className="btn-invite-user">
                                                            <CLink onClick={toggleModalInviteUser}>Invite new users.</CLink>
                                                       </div>
                                                  </CFormGroup>
                                             </CCol>

                                             <CCol lg="6">
                                                  <CFormGroup>
                                                       <CLabel htmlFor="customSubject">
                                                            <div className="title">
                                                                 <div>
                                                                      <strong>Custom Message: </strong>
                                                                 </div>
                                                                 Include a custom subject line for the delivered email, as well as an optional
                                                                 message.
                                                            </div>
                                                       </CLabel>

                                                       <CInput
                                                            id="customSubject"
                                                            name="customSubject"
                                                            placeholder="Custom Subject"
                                                            invalid={!!errors.customSubject && touched.customSubject}
                                                            value={values.customSubject}
                                                            onChange={(e) => setFieldValue('customSubject', e.target.value)}
                                                       />
                                                       <CInvalidFeedback>
                                                            <ErrorMessage name="customSubject" />
                                                       </CInvalidFeedback>
                                                  </CFormGroup>

                                                  <CFormGroup>
                                                       <CTextarea
                                                            id="optionalMessage"
                                                            name="optionalMessage"
                                                            placeholder="Optional message"
                                                            className={`form-control ${
                                                                 !!errors.optionalMessage && touched.optionalMessage ? 'is-invalid' : ''
                                                            }`}
                                                            value={values.optionalMessage}
                                                            onChange={handleChange}
                                                            rows={5}
                                                       />
                                                  </CFormGroup>
                                             </CCol>
                                        </>
                                   )}
                              </CRow>

                              <div className="mt-5">
                                   <CButton type="submit" className="px-4" color="primary" disabled={isSubmitting || !isDataChanged(values, initialValues)} >
                                        {isSubmitting ? <span className="dots-waiting">Waiting</span> : 'Save & Close'}
                                   </CButton>
                              </div>
                         </CForm>
                    );
                }}
            </Formik>

            <InviteUserPopup
                show={modalInviteUser}
                isLoading={privateLoading}
                onClose={closeModalInviteUser}
                event={inviteUser}
                btnText={'Add User'}
            >
                <h2>Invite New User</h2>
                <p>
                    Add a new user to {activeAccount.name}. If they already have a ListenLayer login, we'll automatically grant them access and
                    send them an email notifucation. If they do not yet have a ListenLayer account, we will invite them to create one.
                </p>

                <div className='invite-user-form'>
                    <CLabel>New User's Email:</CLabel>
                    <CInput
                        value={newUserEmail}
                        onChange={handleNewUserEmail}
                        onKeyDown={submitNewUserEmail}
                        data-lpignore='true'
                        invalid={!!validateInviteEmail}
                    />
                    <CInvalidFeedback>{validateInviteEmail}</CInvalidFeedback>
                </div>
            </InviteUserPopup>
        </div>
    );
};

ScheduleTemplate.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attributes: PropTypes.shape({
        schedule: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            schedule: PropTypes.string,
            specificDay: PropTypes.string,
            daysOfWeek: PropTypes.array,
            overrideDateRange: PropTypes.bool,
            emailRecipients: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
            emailSubject: PropTypes.string,
            emailCustomMessage: PropTypes.string,
            dateRangeOverrideSettings: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        }),
        setViewSchedule: PropTypes.func,
        setViewTemplates: PropTypes.func,
        dataSources: PropTypes.array,
        handleSetUnsavedChanges: PropTypes.func,
    }),
};

export default ScheduleTemplate;
